import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux';
import { navigateToLogin, navigateToRegister } from '../../utils/loginRedirect';
import MobileNav from '../MobileNav'
import LanguageDropdown from '../CommonForBoth/TopbarDropdown/LanguageDropdown'
import ProfileMenu from '../CommonForBoth/TopbarDropdown/ProfileMenu'
import WebsiteNotificationBell from '../CommonForBoth/TopbarDropdown/WebsiteNotificationBell'
import GlobalSearchBar from '../Common/GlobalSearchBar'
import { Input } from 'reactstrap'
import mbllogo from "../../assets/images/new_web_logo.png"
import { Search, ArrowLeft } from 'lucide-react'
// info icon removed per design

const MobileHeader = () => {
  const [username, setUsername] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const searchContainerRef = useRef(null)

  // Responsive button style
  const isMobile = windowWidth < 768
  const isSmallMobile = windowWidth < 480
  const isVerySmallMobile = windowWidth < 360
  const mobileNavButtonStyle = {
    background: 'hsla(0, 32%, 89%, 0.04)',
    color: '#E7E9EF',
    border: '1px solid rgba(255,255,255,0.06)',
    padding: isVerySmallMobile ? '6px 10px' : isSmallMobile ? '8px 12px' : isMobile ? '8px 14px' : '10px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: isVerySmallMobile ? 11 : isSmallMobile ? 12 : isMobile ? 13 : 14,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: 'background 150ms, color 150ms, transform 120ms',
    minWidth: 'fit-content',
  }

  const mobileActiveStyle = {
    background: '#ffffff',
    color: '#1F2230',
    border: '1px solid rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  }

  useEffect(() => {
    // try different storage keys and authUser object
    const tryKeys = ['userName', 'username', 'user_fullname', 'fullName', 'name', 'displayName', 'user']
    for (const k of tryKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k)
      if (v) {
        setUsername(v)
        return
      }
    }
    const authUserRaw = localStorage.getItem('authUser') || sessionStorage.getItem('authUser')
    if (authUserRaw) {
      try {
        const obj = JSON.parse(authUserRaw)
        const candidate = obj.displayName || obj.username || obj.name || obj.fullName || obj.email || ''
        if (candidate) setUsername(candidate)
      } catch (e) {
        // ignore
      }
    }
  }, [])

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Mark tutorial as completed when user leaves the keyword explorer page
  useEffect(() => {
    const pathname = location?.pathname || ''
    const isKeywordExplorerPage = pathname === '/keyword-analysis' || pathname.startsWith('/keyword-analysis')

    // Mark tutorial as completed when leaving the keyword explorer page
    if (!isKeywordExplorerPage) {
      try {
        const tutorialSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true'
        const currentStep = sessionStorage.getItem('keywordTutorialCurrentStep')

        // If tutorial is active (not seen and has a current step), mark it as completed
        if (!tutorialSeen && currentStep && (currentStep === '0' || currentStep === '1' || currentStep === '2' || currentStep === '3' || currentStep === '4')) {
          sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true')
          sessionStorage.removeItem('keywordTutorialCurrentStep')
          window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))

          // Try to mark as completed via API (non-blocking)
          fetch('/api/onboarding/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 'keyword_explorer' })
          }).catch(() => null)
        }
      } catch (e) {
        // ignore storage errors
      }
    }
  }, [location?.pathname])

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchExpanded(false)
      }
    }
    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSearchExpanded])

  return (
    <div className='main_mobile'>
      <div className='mbl_hed_section d-flex align-items-center' style={{
        gap: isVerySmallMobile ? 4 : isMobile ? 6 : 16,
        flexWrap: 'nowrap',
        overflow: 'visible',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        position: 'relative',
        zIndex: 1,
        padding: isMobile ? (isVerySmallMobile ? '0 4px' : isSmallMobile ? '0 6px' : '0 8px') : '0'
      }}>
        <MobileNav />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isVerySmallMobile ? 2 : isSmallMobile ? 3 : isMobile ? 4 : 8,
          flex: 1,
          minWidth: 0,
          overflow: 'visible',
          maxWidth: '100%'
        }}>
          {/* KeyQuest Logo */}
          <div
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >
            <div
              role="link"
              tabIndex={0}
              aria-label={username ? "Go to Home" : "Go to Landing page"}
              onClick={() => {
                navigate(username ? '/dashboard' : '/')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(username ? '/dashboard' : '/')
                }
              }}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            >
              <img
                src={mbllogo}
                alt="KeyQuest Logo"
                style={{
                  height: isVerySmallMobile ? '28px' : isSmallMobile ? '34px' : isMobile ? '38px' : '42px',
                  width: 'auto',
                  maxWidth: isVerySmallMobile ? '120px' : isSmallMobile ? '140px' : isMobile ? '160px' : '180px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>

        {/* Login/Signup buttons - show on mobile when user is NOT logged in */}
        {!username && isMobile && (
          <div style={{
            flexShrink: 0,
            marginLeft: 'auto',
            marginRight: isVerySmallMobile ? '4px' : isSmallMobile ? '6px' : '8px',
            display: 'flex',
            gap: isVerySmallMobile ? '4px' : isSmallMobile ? '6px' : '8px',
            alignItems: 'center'
          }}>
            <button
              type="button"
              onClick={() => navigateToLogin(location.pathname, navigate)}
              style={{
                ...mobileNavButtonStyle,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => navigateToRegister(location.pathname, navigate)}
              style={{
                ...mobileNavButtonStyle,
                background: '#F6255A',
                color: '#fff',
                border: 'none'
              }}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Search Icon - only show on mobile when user is logged in */}
        {username && isMobile && (
          <div style={{
            flexShrink: 0,
            marginLeft: 'auto',
            marginRight: isVerySmallMobile ? '6px' : isSmallMobile ? '8px' : '10px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div
              role="button"
              tabIndex={0}
              aria-label="Open search"
              onClick={() => setIsSearchExpanded(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsSearchExpanded(true)
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,

                /* 🔥 Slightly smaller padding */
                padding: isVerySmallMobile ? '4px 6px' : isSmallMobile ? '5px 7px' : '6px 8px',

                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',

                /* 🔥 Fully rounded like expanded search bar */
                borderRadius: 9999,

                /* 🔥 Reduced width to avoid overlapping with logo */
                minWidth: isVerySmallMobile ? 80 : isSmallMobile ? 95 : 110,
                maxWidth: 120,

                /* 🔥 Hug closer to profile icon */
                marginRight: isVerySmallMobile ? '2px' : isSmallMobile ? '3px' : '4px',

                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <Search size={isVerySmallMobile ? 12 : isSmallMobile ? 14 : 16} color="#E7E9EF" />
              <div style={{
                fontSize: isVerySmallMobile ? 10 : isSmallMobile ? 11 : 12,
                color: 'rgba(231,233,239,0.75)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Search
              </div>
            </div>
          </div>
        )}


        {/* Profile Menu - only show when user is logged in, positioned at right end */}
        {username && isMobile && (
          <div style={{
            flexShrink: 0,
            marginRight: isVerySmallMobile ? '4px' : isSmallMobile ? '6px' : '8px'
          }}>
            <style>{`
              .mobile-profile-menu {
                position: relative !important;
                z-index: 9999 !important;
              }
              .mobile-profile-menu .btn {
                padding: 0 !important;
                min-width: auto !important;
              }
              .mobile-profile-menu .btn img {
                width: ${isVerySmallMobile ? '28px' : isSmallMobile ? '32px' : '36px'} !important;
                height: ${isVerySmallMobile ? '28px' : isSmallMobile ? '32px' : '36px'} !important;
              }
              .mobile-profile-menu .btn-light {
                padding: ${isVerySmallMobile ? '2px' : isSmallMobile ? '3px' : '4px'} !important;
                background: transparent !important;
                border: none !important;
              }
              .mobile-profile-menu .dropdown-menu {
                z-index: 10000 !important;
                position: absolute !important;
                right: 0 !important;
                left: auto !important;
                margin-top: 8px !important;
                min-width: 200px !important;
              }
              .mobile-profile-menu .dropdown {
                position: static !important;
              }
            `}</style>
            <div className="mobile-profile-menu">
              <ProfileMenu />
            </div>
          </div>
        )}

        {/* Expandable Search Bar Overlay */}
        {isSearchExpanded && isMobile && (
          <div
            ref={searchContainerRef}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              background: '#1F2230',
              zIndex: 10001,
              padding: isVerySmallMobile ? '8px' : isSmallMobile ? '10px' : '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: isVerySmallMobile ? '8px' : isSmallMobile ? '10px' : '12px'
            }}
          >
            <button
              onClick={() => setIsSearchExpanded(false)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              aria-label="Close search"
            >
              <ArrowLeft
                size={isVerySmallMobile ? 18 : isSmallMobile ? 20 : 22}
                color="#E7E9EF"
              />
            </button>
            <div style={{ flex: 1 }}>
              <GlobalSearchBar
                placeholder="Search apps..."
                style={{
                  maxWidth: '100%',
                  height: isVerySmallMobile ? '38px' : isSmallMobile ? '40px' : '44px'
                }}
              />
            </div>
          </div>
        )}

        {/* Hide search, language, notification, and profile on mobile - only show on desktop/tablet */}
        {!isMobile && (
          <>
            <div
              className="d-none d-md-flex align-items-center"
              style={{ flex: 1, minWidth: "240px", maxWidth: "504px", flexShrink: 1 }}
            >
              <div
                className="position-relative w-100 main_hed_search"
                style={{
                  height: "44px",
                  background: "#21222D",
                  border: "1px solid #353748",
                  borderRadius: "4px",
                }}
              >

                <i className="fa-solid fa-magnifying-glass"></i>
                <Input
                  type="text"
                  className="form-control ps-5"
                  placeholder="Search here ..."
                  aria-label="Search"
                  style={{
                    height: "100%",
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                  }}
                />
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <LanguageDropdown />
            </div>

            {/* Notification Bell Icon */}
            {username && (
              <div style={{ flexShrink: 0 }}>
                <WebsiteNotificationBell />
              </div>
            )}

            <div style={{ flexShrink: 0 }}>
              {username ? (
                <ProfileMenu />
              ) : (
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    style={{
                      ...mobileNavButtonStyle,
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    style={{
                      ...mobileNavButtonStyle,
                      background: '#F6255A',
                      color: '#fff',
                      border: 'none'
                    }}
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* duplicate quick-nav removed (buttons now beside username) */}
    </div>
  )
}

export default MobileHeader
